import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { name: '首页', href: '/' },
  { name: '浪漫爱情', href: '/category/romance' },
  { name: '奇幻冒险', href: '/category/fantasy' },
  { name: '动作热血', href: '/category/action' },
  { name: '悬疑惊悚', href: '/category/thriller' },
  { name: '搞笑日常', href: '/category/comedy' },
  { name: '都市职场', href: '/category/urban' },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setShowSearch(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center">
              <i className="ri-book-read-line text-2xl md:text-3xl text-primary-500"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 tracking-wide">
              韩漫漫画网
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-2 text-sm font-label rounded-lg transition-colors duration-200 whitespace-nowrap ${
                  isActive(link.href)
                    ? 'text-primary-500 font-semibold bg-primary-50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div ref={searchRef} className="relative">
              {showSearch ? (
                <form onSubmit={handleSearch} className="flex items-center">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索漫画..."
                    className="w-48 px-3 py-1.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowSearch(false)}
                    className="ml-2 w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-800 transition-colors"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setShowSearch(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors"
                >
                  <i className="ri-search-line text-xl"></i>
                </button>
              )}
            </div>
            <Link
              to="/about"
              className="px-4 py-2 text-sm font-label font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              关于我们
            </Link>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => {
                setShowSearch(!showSearch);
                setMobileOpen(false);
              }}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600"
            >
              <i className="ri-search-line text-xl"></i>
            </button>
            <button
              onClick={() => {
                setMobileOpen(!mobileOpen);
                setShowSearch(false);
              }}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {showSearch && (
          <div className="md:hidden pb-3">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索漫画..."
                className="w-full px-4 py-2 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400"
                autoFocus
              />
            </form>
          </div>
        )}
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2.5 text-sm rounded-lg transition-colors whitespace-nowrap ${
                  isActive(link.href)
                    ? 'text-primary-500 font-semibold bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="mt-2 px-4 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-full text-center transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}